import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Plus, Pencil, Eye, EyeOff, Activity } from "lucide-react";

export const Route = createFileRoute("/harikaradmin/events/")({
  head: () => ({ meta: [{ title: "Etkinlikler — Admin — JUËN" }] }),
  component: AdminEventsPage,
});

interface EventRow {
  id: string;
  name: string;
  starts_at: string;
  venue: string | null;
  cover_image_url: string | null;
  status: "draft" | "published" | "cancelled";
}

function AdminEventsPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    if (!loading) {
      if (!user) void navigate({ to: "/login" });
      else if (!isAdmin) void navigate({ to: "/dashboard" });
    }
  }, [loading, user, isAdmin, navigate]);

  const load = async () => {
    setLoadingList(true);
    const { data, error } = await supabase
      .from("events")
      .select("id, name, starts_at, venue, cover_image_url, status")
      .order("starts_at", { ascending: false });
    if (error) toast.error(error.message);
    else setEvents((data as EventRow[]) ?? []);
    setLoadingList(false);
  };

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin]);

  const togglePublish = async (ev: EventRow) => {
    const next = ev.status === "published" ? "draft" : "published";
    const { error } = await supabase.from("events").update({ status: next }).eq("id", ev.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(next === "published" ? "Etkinlik yayınlandı" : "Yayından kaldırıldı");
    void load();
  };

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Admin Panel</h1>
            <p className="mt-2 text-muted-foreground">Etkinlikleri yönetin.</p>
          </div>
          <Button asChild>
            <Link to="/harikaradmin/events/$eventId" params={{ eventId: "new" }}>
              <Plus className="mr-1.5 h-4 w-4" /> Yeni etkinlik
            </Link>
          </Button>
        </div>

        <AdminTabs />

        {loadingList ? (
          <div className="py-16 text-center text-muted-foreground">Yükleniyor...</div>
        ) : events.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
            Henüz etkinlik yok. "Yeni etkinlik" ile başlayın.
          </div>
        ) : (
          <div className="space-y-4">
            {events.map((ev) => (
              <div key={ev.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-border bg-card p-4 shadow-card">
                <div className="h-20 w-32 shrink-0 overflow-hidden rounded-lg bg-muted">
                  {ev.cover_image_url ? (
                    <img src={ev.cover_image_url} alt={ev.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">Görsel yok</div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-semibold">{ev.name}</h3>
                    <StatusBadge status={ev.status} />
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      {new Date(ev.starts_at).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" })}
                    </span>
                    {ev.venue && (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5" />
                        {ev.venue}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button asChild variant="outline" size="sm">
                    <Link to="/harikaradmin/monitor/$eventId" params={{ eventId: ev.id }}>
                      <Activity className="mr-1.5 h-4 w-4" /> Detay
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/harikaradmin/events/$eventId" params={{ eventId: ev.id }}>
                      <Pencil className="mr-1.5 h-4 w-4" /> Düzenle
                    </Link>
                  </Button>
                  <Button onClick={() => togglePublish(ev)} variant="outline" size="sm">
                    {ev.status === "published" ? (
                      <><EyeOff className="mr-1.5 h-4 w-4" /> Gizle</>
                    ) : (
                      <><Eye className="mr-1.5 h-4 w-4" /> Yayınla</>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: EventRow["status"] }) {
  const cfg = {
    draft: { label: "Taslak", cls: "bg-muted text-muted-foreground" },
    published: { label: "Yayında", cls: "bg-success/15 text-success" },
    cancelled: { label: "İptal", cls: "bg-destructive/15 text-destructive" },
  }[status];
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cfg.cls}`}>{cfg.label}</span>;
}
